import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { fas } from '@fortawesome/free-solid-svg-icons'

import App from './App.vue'
import router from './router'
import useAuthStore from '@/store/'
import { useSettings } from '@/lib/settings'
import '@/assets/main.scss'

library.add(fas)

const app = createApp(App)
const pinia = createPinia()

app.component('font-awesome-icon', FontAwesomeIcon)
app.use(pinia)

const auth = useAuthStore()
const { fetchSettings } = useSettings()

import { useLegsStore } from '@/store/legsStore'
const legsStore = useLegsStore()
import { useSyncStore } from '@/store/syncStore'
const syncStore = useSyncStore()

async function initializeApp() {
    // Check if we're on an OAuth callback route or have OAuth parameters
    const currentPath = window.location.pathname
    const urlParams = new URLSearchParams(window.location.search)
    const isOAuthCallback = currentPath.startsWith('/auth/') || (urlParams.has('userId') && urlParams.has('secret'))
    
    // If not on OAuth callback, initialize auth normally
    if (!isOAuthCallback) {
        await auth.init()
        await fetchSettings()

        if (auth.isLoggedIn && auth.user.labels.length > 0) {
            await legsStore.fetchLegs()
            await syncStore.fetchLatestSync()
        }
    }

    router.beforeEach((to, from, next) => {
        // Check if this is an OAuth callback with parameters
        const urlParams = new URLSearchParams(window.location.search)
        const hasOAuthParams = urlParams.has('userId') && urlParams.has('secret')
        const hasOAuthError = urlParams.has('error')
        
        // Redirect OAuth callbacks to appropriate handler
        if (hasOAuthParams && to.path === '/') {
            next('/auth/success')
            return
        } else if (hasOAuthError && to.path === '/') {
            next('/auth/failure')
            return
        }
        
        // Allow OAuth callback routes to proceed without auth check
        if (to.path.startsWith('/auth/')) {
            next()
            return
        }

        if (auth.isLoggedIn) {
            if (auth.user.labels.length === 0) { // If logged in without any label (no permissions yet)
                if (to.path === '/pending') {
                    next()
                } else {
                    next('/pending')
                }
            } else if (to.path === '/pending') { // If labeled user lands on the pending page
                next('/')
            } else if (to.path === '/login') { // If logged in trying to access the login page
                next('/')
            } else if (to.path.startsWith('/settings') && !auth.user.labels.includes('admin')) {
                // If trying to access settings without admin privileges
                next('/')
            } else { // If logged in handle normally
                next()
            }
        } else if (to.path === '/login') { // If logged out
            next()
        } else { // If logged out trying to access a protected page
            const loginPath = to.path === '/' ? '/login' : `/login?result=failure&error=${encodeURIComponent(JSON.stringify({ message: 'You must be logged in to access this page.' }))}`
            next(loginPath)
        }
    })

    app.use(router)
    app.mount('#app')
}

initializeApp()